import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Camera, Video, Image as ImageIcon, Mic, FileAudio, FileText, Upload,
  X, RefreshCw, Play, Pause, Square, Trash2, CheckCircle, AlertCircle, Loader2,
} from "lucide-react";
import {
  ATTACH_LIMITS, ACCEPTED, categoryForType, humanSize,
  uploadAttachment, deleteAttachment, pickRecorderMime, recorderSupported,
} from "../../lib/attachments";

const ACCEPT_ATTR = Object.keys(ACCEPTED).join(",");
const uid = () => Math.random().toString(36).slice(2);

const measureDuration = (file, kind) => new Promise((resolve) => {
  try {
    const el = document.createElement(kind === "video" ? "video" : "audio");
    el.preload = "metadata";
    el.onloadedmetadata = () => { const d = el.duration; URL.revokeObjectURL(el.src); resolve(isFinite(d) ? d : null); };
    el.onerror = () => resolve(null);
    el.src = URL.createObjectURL(file);
  } catch { resolve(null); }
});

const makeVideoPoster = (file) => new Promise((resolve) => {
  try {
    const v = document.createElement("video");
    v.preload = "metadata"; v.muted = true; v.playsInline = true;
    v.onloadeddata = () => { try { v.currentTime = Math.min(0.1, v.duration || 0.1); } catch { resolve(null); } };
    v.onseeked = () => {
      try {
        const c = document.createElement("canvas");
        const scale = Math.min(1, 640 / (v.videoWidth || 640));
        c.width = (v.videoWidth || 640) * scale; c.height = (v.videoHeight || 360) * scale;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        URL.revokeObjectURL(v.src);
        resolve(c.toDataURL("image/jpeg", 0.7));
      } catch { resolve(null); }
    };
    v.onerror = () => resolve(null);
    v.src = URL.createObjectURL(file);
  } catch { resolve(null); }
});

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const AttachmentUploader = ({ uploadSessionId, accessToken, onStateChange, disabled }) => {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [recorder, setRecorder] = useState(null); // 'audio' | 'video' | null
  const inputs = {
    photoCapture: useRef(null), videoCapture: useRef(null), photoLib: useRef(null),
    videoLib: useRef(null), audioLib: useRef(null), file: useRef(null),
  };
  const [dragOver, setDragOver] = useState(false);

  const totalBytes = items.filter(i => i.status !== "failed").reduce((s, i) => s + (i.size || 0), 0);
  const count = items.filter(i => i.status !== "failed").length;

  useEffect(() => {
    const uploading = items.some(i => i.status === "uploading" || i.status === "pending");
    const failed = items.some(i => i.status === "failed");
    const ready = items.filter(i => i.status === "success").length;
    onStateChange && onStateChange({ uploading, failed, ready, total: count });
  }, [items, count, onStateChange]);

  const setItem = (localId, patch) => setItems(prev => prev.map(i => i.localId === localId ? { ...i, ...patch } : i));

  const beginUpload = useCallback(async (item) => {
    setItem(item.localId, { status: "uploading", progress: 0, error: "" });
    try {
      const row = await uploadAttachment({
        file: item.file, uploadSessionId, durationSeconds: item.durationSeconds,
        thumbnailDataUrl: item.thumbnailDataUrl, accessToken,
        onProgress: (p) => setItem(item.localId, { progress: p }),
      });
      setItem(item.localId, { status: "success", serverId: row.id, progress: 100 });
    } catch (e) {
      setItem(item.localId, { status: "failed", error: e.message || "Upload failed" });
    }
  }, [uploadSessionId, accessToken]);

  const addFiles = useCallback(async (fileList) => {
    setMsg("");
    const files = Array.from(fileList || []);
    for (const file of files) {
      const category = categoryForType(file.type);
      if (!category) { setMsg(`"${file.name}" is not a supported file type.`); continue; }
      if (count >= ATTACH_LIMITS.maxFiles) { setMsg(`You can attach up to ${ATTACH_LIMITS.maxFiles} files.`); break; }
      if (file.size > ATTACH_LIMITS.perType[category]) {
        setMsg(`"${file.name}" is too large (max ${ATTACH_LIMITS.perType[category] / 1048576} MB for ${category}).`); continue;
      }
      let durationSeconds = null, thumbnailDataUrl = null;
      if (category === "video" || category === "audio") {
        durationSeconds = await measureDuration(file, category);
        if (durationSeconds != null && durationSeconds > ATTACH_LIMITS.maxDurationSeconds + 0.5) {
          setMsg(`"${file.name}" is longer than ${ATTACH_LIMITS.maxDurationSeconds}s. Please choose a shorter ${category}.`); continue;
        }
        // Note: iPhone Safari often can't read the duration of a .mov/.mp4 from
        // the Photo Library via a <video> element (returns null). Do NOT reject
        // in that case — the file is still valid and the backend enforces size
        // and format. We simply upload without a client-side duration/length.
        if (category === "video") thumbnailDataUrl = await makeVideoPoster(file);
      }
      if (totalBytes + file.size > ATTACH_LIMITS.maxCombinedBytes) {
        setMsg(`Adding "${file.name}" would exceed the 100 MB total limit.`); break;
      }
      const previewUrl = category === "image" ? URL.createObjectURL(file) : null;
      const item = { localId: uid(), file, category, name: file.name, size: file.size, status: "pending", progress: 0, previewUrl, durationSeconds, thumbnailDataUrl, error: "", serverId: null, playing: false };
      setItems(prev => [...prev, item]);
      beginUpload(item);
    }
  }, [count, totalBytes, beginUpload]);

  const removeItem = async (item) => {
    if (item.serverId) { try { await deleteAttachment(item.serverId, accessToken); } catch { /* ignore */ } }
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    setItems(prev => prev.filter(i => i.localId !== item.localId));
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (!disabled) addFiles(e.dataTransfer.files); };

  const remaining = ATTACH_LIMITS.maxFiles - count;

  const pickerBtns = [
    { label: "Take Photo", icon: Camera, ref: inputs.photoCapture, testid: "attach-take-photo" },
    { label: "Record Video", icon: Video, ref: inputs.videoCapture, testid: "attach-record-video-native" },
    { label: "Photo Library", icon: ImageIcon, ref: inputs.photoLib, testid: "attach-photo-library" },
    { label: "Choose Video", icon: Video, ref: inputs.videoLib, testid: "attach-choose-video" },
    { label: "Record Voice Note", icon: Mic, onClick: () => setRecorder("audio"), testid: "attach-record-voice" },
    { label: "Choose Audio", icon: FileAudio, ref: inputs.audioLib, testid: "attach-choose-audio" },
    { label: "Choose File", icon: FileText, ref: inputs.file, testid: "attach-choose-file" },
  ];

  return (
    <div data-testid="attachment-uploader" className="attach-uploader">
      {/* Hidden native inputs */}
      <input ref={inputs.photoCapture} type="file" accept="image/*" capture="environment" hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={inputs.videoCapture} type="file" accept="video/*" capture="environment" hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={inputs.photoLib} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={inputs.videoLib} type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/webm" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={inputs.audioLib} type="file" accept="audio/*" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={inputs.file} type="file" accept={ACCEPT_ATTR} multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />

      <div
        className={`attach-dropzone ${dragOver ? "over" : ""}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{ border: "1.5px dashed var(--border-color)", borderRadius: "var(--radius-md)", padding: "16px", background: dragOver ? "var(--surface-hover)" : "transparent" }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "8px" }}>
          {pickerBtns.map(b => (
            <button
              key={b.label} type="button" disabled={disabled || remaining <= 0}
              onClick={() => (b.onClick ? b.onClick() : b.ref.current?.click())}
              data-testid={b.testid}
              className="btn-outline"
              style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center", fontSize: "0.8125rem", padding: "10px 8px" }}
              aria-label={b.label}
            >
              <b.icon size={16} aria-hidden="true" /> {b.label}
            </button>
          ))}
        </div>
        <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-muted)", margin: "10px 0 0" }}>
          Or drag &amp; drop files here (desktop). {remaining} of {ATTACH_LIMITS.maxFiles} attachments left · {humanSize(totalBytes)} / 100 MB used
        </p>
      </div>

      <p aria-live="polite" data-testid="attach-message" style={{ minHeight: "18px", margin: "8px 0 0", fontSize: "0.8125rem", color: "#b45309" }}>{msg}</p>

      {/* Items */}
      {items.length > 0 && (
        <div data-testid="attach-list" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "10px", marginTop: "12px" }}>
          {items.map(item => (
            <AttachmentTile key={item.localId} item={item} accessToken={accessToken} onRemove={() => removeItem(item)} onRetry={() => beginUpload(item)} />
          ))}
        </div>
      )}

      {recorder === "audio" && <VoiceRecorder onClose={() => setRecorder(null)} onKeep={(file, dur) => { setRecorder(null); addFiles([file]); void dur; }} />}
      {recorder === "video" && <VideoRecorderPanel onClose={() => setRecorder(null)} onKeep={(file) => { setRecorder(null); addFiles([file]); }} />}
    </div>
  );
};

const statusIcon = (s) => s === "uploading" || s === "pending"
  ? <Loader2 size={14} className="spin" aria-hidden="true" />
  : s === "success" ? <CheckCircle size={14} color="#059669" aria-hidden="true" />
  : s === "failed" ? <AlertCircle size={14} color="#dc2626" aria-hidden="true" /> : null;

const AttachmentTile = ({ item, onRemove, onRetry }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const localAudio = item.category === "audio" ? URL.createObjectURL(item.file) : null;
  useEffect(() => () => { if (localAudio) URL.revokeObjectURL(localAudio); }, [localAudio]);

  return (
    <div data-testid="attach-item" style={{ border: "1px solid var(--border-color)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--card-bg)" }}>
      <div style={{ height: "96px", background: "#0f172a10", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        {item.category === "image" && item.previewUrl && <img src={item.previewUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
        {item.category === "video" && (item.thumbnailDataUrl
          ? <><img src={item.thumbnailDataUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /><Play size={26} color="#fff" style={{ position: "absolute" }} /></>
          : <Video size={26} color="var(--text-muted)" />)}
        {item.category === "audio" && (
          <button type="button" aria-label={playing ? "Pause" : "Play"} data-testid="attach-audio-play"
            onClick={() => { const a = audioRef.current; if (!a) return; if (playing) { a.pause(); } else { a.play(); } }}
            className="btn-outline" style={{ padding: "8px" }}>
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </button>
        )}
        {item.category === "document" && <FileText size={26} color="var(--text-muted)" />}
        {item.category === "audio" && <audio ref={audioRef} src={localAudio} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />}
      </div>
      <div style={{ padding: "8px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-dark)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.name}>
          {item.category === "audio" && item.name?.startsWith("voice-note") ? "Voice note" : item.name}
        </div>
        <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "5px", marginTop: "2px" }}>
          {statusIcon(item.status)}
          <span>{item.status === "uploading" ? `${item.progress}%` : item.status === "success" ? "Uploaded" : item.status === "failed" ? "Failed" : "Waiting"}</span>
          <span>· {humanSize(item.size)}</span>
          {item.durationSeconds != null && <span>· {fmtTime(item.durationSeconds)}</span>}
        </div>
        {item.status === "uploading" && (
          <div style={{ height: "3px", background: "var(--border-color)", borderRadius: "2px", marginTop: "6px" }}>
            <div style={{ height: "100%", width: `${item.progress}%`, background: "var(--accent)", borderRadius: "2px", transition: "width .2s" }} />
          </div>
        )}
        {item.status === "failed" && <div style={{ fontSize: "0.6875rem", color: "#dc2626", marginTop: "4px" }}>{item.error}</div>}
        <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
          {item.status === "failed" && (
            <button type="button" onClick={onRetry} data-testid="attach-retry" className="btn-outline" style={{ fontSize: "0.6875rem", padding: "4px 8px", display: "flex", alignItems: "center", gap: "4px" }}>
              <RefreshCw size={12} /> Retry
            </button>
          )}
          <button type="button" onClick={onRemove} data-testid="attach-remove" className="btn-outline" style={{ fontSize: "0.6875rem", padding: "4px 8px", display: "flex", alignItems: "center", gap: "4px", color: "#b91c1c" }}>
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Voice recorder ─────────────────────────────────────────────────── */
const VoiceRecorder = ({ onClose, onKeep }) => {
  const [state, setState] = useState("idle"); // idle | recording | recorded | denied | unsupported
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const mediaRef = useRef(null), chunksRef = useRef([]), streamRef = useRef(null), timerRef = useRef(null), blobRef = useRef(null), mimeRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);
  useEffect(() => () => { cleanup(); if (blobUrl) URL.revokeObjectURL(blobUrl); }, [cleanup, blobUrl]);

  useEffect(() => { if (!recorderSupported()) setState("unsupported"); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickRecorderMime("audio");
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mimeRef.current = mr.mimeType || mime || "audio/mp4";
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const actual = mr.mimeType || mimeRef.current || "audio/mp4";
        mimeRef.current = actual;
        const type = actual.split(";")[0];
        const blob = new Blob(chunksRef.current, { type });
        blobRef.current = blob; setBlobUrl(URL.createObjectURL(blob)); setState("recorded");
        cleanup();
      };
      mediaRef.current = mr; mr.start(); setState("recording"); setElapsed(0);
      if (mr.mimeType) mimeRef.current = mr.mimeType;
      timerRef.current = setInterval(() => setElapsed(e => {
        const n = e + 1; if (n >= 60) { try { mr.stop(); } catch {} } return n;
      }), 1000);
    } catch (e) {
      setState(e?.name === "NotAllowedError" ? "denied" : "unsupported"); cleanup();
    }
  };
  const stop = () => { try { mediaRef.current?.stop(); } catch {} };
  const keep = () => {
    const m = (mimeRef.current || "audio/mp4").toLowerCase();
    const ext = m.includes("mp4") ? "m4a" : m.includes("mpeg") ? "mp3" : m.includes("aac") ? "aac" : m.includes("ogg") ? "ogg" : m.includes("wav") ? "wav" : "webm";
    const type = m.split(";")[0];
    const file = new File([blobRef.current], `voice-note.${ext}`, { type });
    onKeep(file, elapsed);
  };

  return (
    <RecorderShell title="Record Voice Note" onClose={onClose} testid="voice-recorder">
      {state === "unsupported" && <p style={{ fontSize: "0.875rem" }}>Recording isn’t supported in this browser. You can still use <strong>Choose Audio</strong> to upload a file.</p>}
      {state === "denied" && <p style={{ fontSize: "0.875rem" }}>Microphone permission is needed only to record a voice note. You can continue without it, or use <strong>Choose Audio</strong> instead.</p>}
      {(state === "idle") && (
        <button type="button" className="btn-primary" data-testid="voice-start" onClick={start} style={{ display: "flex", gap: "8px", alignItems: "center" }}><Mic size={16} /> Start Recording</button>
      )}
      {state === "recording" && (
        <div style={{ textAlign: "center" }}>
          <div aria-live="polite" style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "center", color: "#dc2626", fontWeight: 600 }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#dc2626", display: "inline-block" }} className="pulse" /> Recording {fmtTime(elapsed)} / 1:00
          </div>
          <button type="button" className="btn-outline" data-testid="voice-stop" onClick={stop} style={{ marginTop: "12px", display: "inline-flex", gap: "6px", alignItems: "center" }}><Square size={14} /> Stop</button>
        </div>
      )}
      {state === "recorded" && (
        <div style={{ textAlign: "center" }}>
          <audio controls src={blobUrl} data-testid="voice-preview" style={{ width: "100%" }} />
          <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "12px", flexWrap: "wrap" }}>
            <button type="button" className="btn-outline" data-testid="voice-rerecord" onClick={() => { if (blobUrl) URL.revokeObjectURL(blobUrl); setBlobUrl(null); setState("idle"); }}><RefreshCw size={14} /> Re-record</button>
            <button type="button" className="btn-primary" data-testid="voice-keep" onClick={keep}><CheckCircle size={14} /> Keep</button>
          </div>
        </div>
      )}
    </RecorderShell>
  );
};

/* ── Video recorder ─────────────────────────────────────────────────── */
const VideoRecorderPanel = ({ onClose, onKeep }) => {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const liveRef = useRef(null), mediaRef = useRef(null), chunksRef = useRef([]), streamRef = useRef(null), timerRef = useRef(null), blobRef = useRef(null), mimeRef = useRef(null);
  const cleanup = useCallback(() => { if (timerRef.current) clearInterval(timerRef.current); if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }, []);
  useEffect(() => () => { cleanup(); if (blobUrl) URL.revokeObjectURL(blobUrl); }, [cleanup, blobUrl]);
  useEffect(() => { if (!recorderSupported()) setState("unsupported"); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: true });
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play().catch(() => {}); }
      const mime = pickRecorderMime("video");
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mimeRef.current = mr.mimeType || mime || "video/mp4";
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => { const actual = mr.mimeType || mimeRef.current || "video/mp4"; mimeRef.current = actual; const type = actual.split(";")[0]; const blob = new Blob(chunksRef.current, { type }); blobRef.current = blob; setBlobUrl(URL.createObjectURL(blob)); setState("recorded"); cleanup(); };
      mediaRef.current = mr; mr.start(); setState("recording"); setElapsed(0);
      if (mr.mimeType) mimeRef.current = mr.mimeType;
      timerRef.current = setInterval(() => setElapsed(e => { const n = e + 1; if (n >= 60) { try { mr.stop(); } catch {} } return n; }), 1000);
    } catch (e) { setState(e?.name === "NotAllowedError" ? "denied" : "unsupported"); cleanup(); }
  };
  const stop = () => { try { mediaRef.current?.stop(); } catch {} };
  const keep = () => { const m = (mimeRef.current || "video/mp4").toLowerCase(); const ext = m.includes("mp4") ? "mp4" : m.includes("quicktime") ? "mov" : m.includes("m4v") ? "m4v" : "webm"; const type = m.split(";")[0]; onKeep(new File([blobRef.current], `video-recording.${ext}`, { type })); };

  return (
    <RecorderShell title="Record Video" onClose={onClose} testid="video-recorder">
      {state === "unsupported" && <p style={{ fontSize: "0.875rem" }}>In-browser video recording isn’t supported here. Use <strong>Record Video</strong> (device camera) or <strong>Choose Video</strong> instead.</p>}
      {state === "denied" && <p style={{ fontSize: "0.875rem" }}>Camera/microphone permission is needed only to record. You can continue without it, or use <strong>Choose Video</strong>.</p>}
      {state !== "recorded" && state !== "unsupported" && state !== "denied" && (
        <div style={{ textAlign: "center" }}>
          <video ref={liveRef} muted playsInline style={{ width: "100%", maxHeight: "260px", background: "#000", borderRadius: "8px" }} />
          {state === "idle" && <button type="button" className="btn-primary" data-testid="video-start" onClick={start} style={{ marginTop: "12px" }}><Video size={16} /> Start Recording</button>}
          {state === "recording" && (
            <div style={{ marginTop: "12px" }}>
              <div aria-live="polite" style={{ color: "#dc2626", fontWeight: 600 }}>● Recording {fmtTime(elapsed)} / 1:00</div>
              <button type="button" className="btn-outline" data-testid="video-stop" onClick={stop} style={{ marginTop: "8px" }}><Square size={14} /> Stop</button>
            </div>
          )}
        </div>
      )}
      {state === "recorded" && (
        <div style={{ textAlign: "center" }}>
          <video controls src={blobUrl} data-testid="video-preview" style={{ width: "100%", maxHeight: "260px", borderRadius: "8px" }} />
          <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "12px", flexWrap: "wrap" }}>
            <button type="button" className="btn-outline" data-testid="video-rerecord" onClick={() => { if (blobUrl) URL.revokeObjectURL(blobUrl); setBlobUrl(null); setState("idle"); }}><RefreshCw size={14} /> Re-record</button>
            <button type="button" className="btn-primary" data-testid="video-keep" onClick={keep}><CheckCircle size={14} /> Keep</button>
          </div>
        </div>
      )}
    </RecorderShell>
  );
};

const RecorderShell = ({ title, onClose, children, testid }) => (
  <div role="dialog" aria-label={title} data-testid={testid}
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{ background: "var(--card-bg, #fff)", borderRadius: "12px", padding: "20px", width: "100%", maxWidth: "440px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: 700, margin: 0 }}>{title}</h3>
        <button type="button" aria-label="Close" data-testid="recorder-close" onClick={onClose} className="btn-outline" style={{ padding: "6px" }}><X size={16} /></button>
      </div>
      {children}
    </div>
  </div>
);

export default AttachmentUploader;
